"use client";

import { motion } from "framer-motion";
import { Icon, type IconName } from "@/components/ui/icon";
import { cn } from "@/lib/utils";

export type FilterPanelPane = "filters" | "criteria";

const PANES: { id: FilterPanelPane; label: string; icon: IconName }[] = [
  { id: "filters", label: "Filters", icon: "IconFilter2" },
  { id: "criteria", label: "Criteria", icon: "IconListSparkle" },
];

/**
 * Segmented control in the filter panel header that swaps the panel body
 * between the filter sections and the ranking-criteria editor.
 */
export function FilterPanelPaneSwitcher({
  value,
  onValueChange,
  counts,
  className,
}: {
  value: FilterPanelPane;
  onValueChange: (next: FilterPanelPane) => void;
  counts: Partial<Record<FilterPanelPane, number>>;
  className?: string;
}) {
  return (
    <div
      role="tablist"
      aria-label="Panel view"
      className={cn(
        "flex h-9 w-full min-w-0 items-center overflow-hidden rounded-lg border border-border bg-contrast/3",
        className,
      )}
    >
      {PANES.map((pane) => {
        const active = pane.id === value;
        const count = counts[pane.id] ?? 0;
        return (
          <button
            key={pane.id}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onValueChange(pane.id)}
            className={cn(
              "relative flex h-full min-w-0 flex-1 cursor-pointer items-center justify-center gap-1.5 px-2 transition-colors",
              !active && "hover:bg-contrast/[0.04]",
            )}
          >
            {active && (
              <motion.span
                layoutId="filter-panel-pane"
                className="absolute inset-0 bg-contrast/8 dark:bg-black/30"
                transition={{
                  type: "spring",
                  damping: 30,
                  stiffness: 500,
                  mass: 0.5,
                }}
              />
            )}
            <span
              className={cn(
                "relative z-10 shrink-0 transition-opacity",
                active ? "opacity-90" : "opacity-50",
              )}
            >
              <Icon name={pane.icon} size={15} className="text-heading" />
            </span>
            <span
              className={cn(
                "relative z-10 truncate text-[13px] font-medium transition-colors",
                active ? "text-heading" : "text-contrast/65",
              )}
            >
              {pane.label}
            </span>
            {count > 0 && (
              <span
                className={cn(
                  "relative z-10 flex h-[18px] min-w-[18px] items-center justify-center rounded border-[0.714px] px-1 text-[11px] font-medium tabular-nums leading-none",
                  active
                    ? "border-contrast/15 bg-contrast/4 text-contrast/65"
                    : "border-[#ffaf53]/25 bg-[#ffaf53]/15 text-[#ffaf53]",
                )}
              >
                {count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
